import React, { useContext, useEffect, useState } from 'react';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import { useHashAsState } from 'tbk-components/src/hooks';
import ElasticMapper from 'tbk-components/src/services/ElasticMapper';
import qs from 'qs';
import Loading from 'tbk-components/src/components/Loading';
import FilterBar from 'tbk-components/src/components/BlogRoll/BlogRollFilterBar';
import BlogRollTile from './BlogRollTile';
import BlogRollPagination from './BlogRollPagination';
import cn from 'classnames';
import { IImage } from 'tbk-components/src/components/Image';

export interface IBlogAuthor {
	name?: string;
	url?: string;
	image?: IImage;
}

export interface IBlogPostCategory {
	name?: string;
	url?: string;
}

export interface IBlogPostTag {
	name?: string;
	url?: string;
}

export interface IBlogPost {
	image?: IImage;
	heading?: string;
	author?: IBlogAuthor;
	readTime?: string;
	excerpt?: string;
	content?: string;
	url?: string;
	categories?: IBlogPostCategory[];
	tags?: IBlogPostTag[];
	date?: string;
}

export interface IBlogRollProps extends IBaseElementProps {
	context: any;
	heading?: string;
	archivePage?: string;
	postsPerRow?: 3 | 4;
	showFilterHeader?: boolean;
	showHeading?: boolean;
	showSeeMore?: boolean;
	seeMoreLabel?: string;
	showPagination?: boolean;
	showExcerpt?: boolean;
	showButton?: boolean;
	showByline?: boolean;
	posts?: {
		total: number;
		hits: any[];
	};
	fetchArgs?: any;
	filterBarOnly?: boolean;
	filterBarTargetUrl?: string;
	noResultsMessage?: string;
}

const BlogRoll: React.FC<IBlogRollProps> = ({
	className,
	classNames = [],
	context,
	heading,
	archivePage,
	postsPerRow = 3,
	showFilterHeader = true,
	showHeading = false,
	showSeeMore = false,
	seeMoreLabel = 'Read On',
	showPagination = true,
	showExcerpt = true,
	showButton = true,
	showByline= true,
	posts,
	fetchArgs,
	filterBarOnly = false,
	filterBarTargetUrl = '',
	noResultsMessage = 'No results found.  Please adjust your filters',
}) => {
	// @ts-ignore
	const { assets: contextAssets, Api } = useContext(context);
	const [assets, setAssets] = useState<any>(null);
	const [blogRoll, setBlogRoll] = useState<any>(null);
	const [termMap, setTermMap] = useState<any>(null);
	const [total, setTotal] = useState<number | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [filterBarState, setFilterBarState] = useState({});
	const taxonomy_parameters = ((taxonomies) => {
		const t: any = {};
		(taxonomies || []).forEach((tax: any) => {
			t[tax.taxonomy] = 'csv';
		});
		return t;
	})(assets?.taxonomies);
	const [state, hash, updateState, normalizeState] = useHashAsState({
		parameters: {
			page: 'int',
			...taxonomy_parameters,
			search: 'string',
		},
		defaults: {
			page: 1,
		},
	});

	// Here's the effect that actually gets the products from the server whenever the search has changed.
	useEffect(() => {
		if (posts) {
			setTotal(posts.total);
			setBlogRoll(ElasticMapper(posts));
			return;
		}
		if (!assets) {
			return;
		}
		let args = qs.parse(hash.replace(/^#!/, ''), {
			ignoreQueryPrefix: true,
		});
		args.posts_per_page = assets?.posts_per_page;
		if (fetchArgs) {
			args = {
				...fetchArgs,
				...args,
			};
		}
		setBlogRoll(null);
		setLoading(true);
		Api.getBlogRoll(args)
			.then((results: any) => {
				setTotal(results.total);
				setBlogRoll(ElasticMapper(results));
			})
			.finally(() => {
				setLoading(false);
			});
	}, [hash, setBlogRoll, setTotal, assets, fetchArgs, posts, Api]);

	useEffect(() => {
		const setAssetsAndTermMap = (results: any) => {
			setAssets(results);
			const map: any = {};
			results.taxonomies.forEach((taxonomy: any) => {
				taxonomy.terms.forEach((term: any) => {
					map[term.term_id] = term;
				});
			});
			setTermMap(map);
		};
		if (contextAssets) {
			setAssetsAndTermMap(contextAssets);
		} else {
			Api.getBlogRollAssets(fetchArgs).then((results: any) => {
				setAssetsAndTermMap(results);
			});
		}
	}, [setAssets, setTermMap, contextAssets, Api, fetchArgs]);

	if (!assets) {
		return (
			<div className="loading-wrapper">
				<Loading />
			</div>
		);
	} else if (filterBarOnly) {
		const onChangeFilterBar = (change: any) => {
			setFilterBarState((s) => {
				return { ...s, ...change };
			});
		};
		return (
			<FilterBar
				filters={filterBarState}
				assets={assets}
				targetUrl={
					filterBarTargetUrl + normalizeState(filterBarState, 'query')
				}
				onChange={onChangeFilterBar}
			/>
		);
	}

	const includeHeader = !posts ? showFilterHeader : false; // if we already have posts, we're not going to include the header

	// @ts-ignore
	const elClassName = className || cn(...classNames);
	return (
		<div className={elClassName}>
			{includeHeader && (
				<FilterBar
					filters={state}
					assets={assets}
					onChange={updateState}
				/>
			)}
			<div className="blogroll-results__container">
				<div className="blogroll-results__container-inner">
					{loading ? (
						<div className="loading-wrapper">
							<Loading />
						</div>
					) : (
						<React.Fragment>
							{showHeading || showSeeMore ? (
								<div className="blogroll-results__heading">
									{showHeading && heading ? (
										<h2>{heading}</h2>
									) : null}
									{showSeeMore && archivePage ? (
										<a
											className="btn btn-primary btn-sm"
											href={archivePage}
										>
											{seeMoreLabel}
										</a>
									) : null}
								</div>
							) : (
								''
							)}
							{total ? (
								<React.Fragment>
									<div className="blogroll-results__list" data-posts-per-row={postsPerRow}>
										{(blogRoll || []).map(
											(blogroll: any) => (
												<BlogRollTile
													key={blogroll.ID}
													blogroll={blogroll}
													showExcerpt={showExcerpt}
													showButton={showButton}
													showByline={showByline}
													termMap={termMap}
												/>
											),
										)}
									</div>
									{showPagination && (
										<BlogRollPagination
											className="pagination-wrapper"
											page={state.page || 1}
											perPage={
												fetchArgs?.posts_per_page ||
												assets?.posts_per_page
											}
											total={total}
											onChange={(page: any) =>
												updateState({ page })
											}
										/>
									)}
								</React.Fragment>
							) : (
								<p className="no-results">{noResultsMessage}</p>
							)}
						</React.Fragment>
					)}
				</div>
			</div>
		</div>
	);
};

export default withAppContext(BlogRoll);
